import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createIntakeForm,
  fetchIntakeForms,
} from "../../store/thunks/intakeThunk";

import Table from "../../applicationUi/components/Table";
import IntakeDetailsModal from "./IntakeDetailsModal";
import { Dialog, Transition } from "@headlessui/react";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function IntakesPage() {
  const dispatch = useDispatch();
  const intakes = useSelector((state) =>
    Array.isArray(state.intake.allIntakeForms)
      ? state.intake.allIntakeForms
      : []
  );
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [isUrlModalOpen, setIsUrlModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [useExistingClient, setUseExistingClient] = useState(false);
  const [isCreatingIntake, setIsCreatingIntake] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientInfo, setNewClientInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    dispatch(fetchIntakeForms());
  }, [dispatch, selectedIntake, isUrlModalOpen]);

  const user = useSelector((state) => state.auth.user);

  const filteredIntakes = useMemo(() => {
    if (statusFilter === "all") return intakes;

    const statusGroups = {
      pending: ["pending client", "pending approval", "client completed"],
      approved: [
        "approved - no deposit",
        "approved - deposit received",
        "approved",
      ],
      rejected: ["rejected"],
    };

    return intakes.filter((intake) => {
      const currentStatus =
        intake.statusHistory?.[intake.statusHistory.length - 1]?.status;
      return statusGroups[statusFilter]?.includes(currentStatus);
    });
  }, [intakes, statusFilter]);

  const handleCreateIntake = async (client = null) => {
    try {
      const payload = client
        ? {
            client: {
              firstName: client.firstName,
              lastName: client.lastName,
              dob: client.dateOfBirth,
              address: {
                aptUnit: client.address?.aptUnit || "",
                streetNumber: client.address?.streetNumber || "",
                streetName: client.address?.streetName || "",
                city: client.address?.city || "",
                state: client.address?.state || "",
                zipCode: client.address?.zipCode || "",
                country: client.address?.country || "United States",
              },
            },
            createdClient: client._id,
            createdBy: user._id,
          }
        : { createdBy: user._id };

      console.log("Payload being sent to API:", payload);
      const result = await dispatch(createIntakeForm(payload));
      const intakeId = result.payload._id;

      if (intakeId) {
        const url = `${window.location.origin}/intake/${intakeId}`;
        setGeneratedUrl(url);
        setIsUrlModalOpen(true);
        console.log("Generated URL:", url);
      }
    } catch (error) {
      console.error("Error in handleCreateIntake:", error);
    }
  };

  const handleCreateNewClientIntake = async (clientInfo = null) => {
    try {
      const payload = { 
        createdBy: user._id,
        ...(clientInfo && {
          client: {
            firstName: clientInfo.firstName || "",
            lastName: clientInfo.lastName || "",
            email: clientInfo.email || "",
            phone: clientInfo.phone || "",
          }
        })
      };

      console.log("Client info received:", clientInfo);
      console.log("Payload for new client intake being sent to API:", JSON.stringify(payload, null, 2));
      
      // Check if we have any client data
      if (clientInfo && (clientInfo.firstName || clientInfo.lastName || clientInfo.email || clientInfo.phone)) {
        console.log("Sending intake with client data:", {
          firstName: clientInfo.firstName,
          lastName: clientInfo.lastName,
          email: clientInfo.email,
          phone: clientInfo.phone
        });
      } else {
        console.log("No client data provided, creating empty intake");
      }

      const result = await dispatch(createIntakeForm(payload));
      const intakeId = result.payload._id;

      if (intakeId) {
        const url = `${window.location.origin}/intake/${intakeId}`;
        setGeneratedUrl(url);
        setIsUrlModalOpen(true);
        console.log("Generated URL for new client:", url);
      }
    } catch (error) {
      console.error("Error in handleCreateNewClientIntake:", error);
    }
  };

  const handleCreateExistingClientIntake = async (client) => {
    if (isCreatingIntake) return;
    setIsCreatingIntake(true);

    try {
      const payload = {
        client: {
          firstName: client.firstName,
          lastName: client.lastName,
          dob: client.dateOfBirth,
          address: {
            aptUnit: client.address?.aptUnit || "",
            streetNumber: client.address?.streetNumber || "",
            streetName: client.address?.streetName || "",
            city: client.address?.city || "",
            state: client.address?.state || "",
            zipCode: client.address?.zipCode || "",
            country: client.address?.country || "United States",
          },
        },
        createdClient: client._id,
        createdBy: user._id,
      };

      console.log("Payload for existing client intake:", payload);

      const result = await dispatch(createIntakeForm(payload));
      const intakeId = result.payload?._id;

      if (intakeId) {
        const url = `${window.location.origin}/intake/${intakeId}`;
        setGeneratedUrl(url);
        setIsUrlModalOpen(true);
        console.log("Generated URL for existing client:", url);
      }
    } catch (error) {
      console.error("Error in handleCreateExistingClientIntake:", error);
    } finally {
      setTimeout(() => setIsCreatingIntake(false), 500);
    }
  };

  const handleRowClick = (intakeId) => {
    const intake = intakes.find((item) => item._id === intakeId);
    setSelectedIntake(intake);
  };

  const handleModalClose = () => {
    setSelectedIntake(null);
  };

  const handleUrlModalClose = () => {
    setIsUrlModalOpen(false);
    setGeneratedUrl("");
  };

  const handleNewClientFormSubmit = () => {
    // Create intake with the provided client info
    handleCreateNewClientIntake(newClientInfo);
    
    // Reset form and close modal
    setNewClientInfo({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
    setShowNewClientForm(false);
    setIsCreateModalOpen(false);
  };

  const handleNewClientFormCancel = () => {
    // Reset form and go back to main modal
    setNewClientInfo({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
    setShowNewClientForm(false);
  };

  const columns = useMemo(
    () => [
      {
        header: "Created Date",
        accessor: (row) => {
          let createdDate = null;
          
          // Debug logging to see what data we're working with
          console.log("Processing intake:", {
            id: row._id,
            createdAt: row.createdAt,
            statusHistory: row.statusHistory
          });
          
          // For existing intakes without createdAt, use the first status history entry
          if (row.statusHistory && row.statusHistory.length > 0) {
            // Find the first "pending client" status entry (the initial creation)
            const pendingClientStatus = row.statusHistory.find(
              (status) => status.status === "pending client"
            );
            console.log("Found pending client status:", pendingClientStatus);
            
            if (pendingClientStatus && pendingClientStatus.modifiedAt) {
              createdDate = new Date(pendingClientStatus.modifiedAt);
              console.log("Using modifiedAt date:", pendingClientStatus.modifiedAt, "->", createdDate);
            }
          }
          
          // Fallback to createdAt field if available (for newer intakes)
          if (!createdDate && row.createdAt) {
            createdDate = new Date(row.createdAt);
            console.log("Using createdAt date:", row.createdAt, "->", createdDate);
          }
          
          if (!createdDate) {
            console.log("No creation date found for intake:", row._id, "- using fallback date");
            createdDate = new Date("2024-12-01"); // Fallback date
          }
          
          const formattedDate = createdDate.toLocaleDateString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          console.log("Final formatted date:", formattedDate);
          
          return formattedDate;
        },
      },
      { 
        header: "First Name", 
        accessor: (row) => {
          // Use initial client data if available (for pending intakes)
          // Otherwise fall back to client data (for completed intakes)
          return row.initialClientFirstName || row.client?.firstName || "";
        }
      },
      { 
        header: "Last Name", 
        accessor: (row) => {
          return row.initialClientLastName || row.client?.lastName || "";
        }
      },
      { 
        header: "Client Email", 
        accessor: (row) => {
          return row.initialClientEmail || row.client?.email || "";
        }
      },
      { 
        header: "Phone", 
        accessor: (row) => {
          return row.initialClientPhone || row.client?.phone || "";
        }
      },
      {
        header: "Date of Birth",
        accessor: (row) => {
          if (!row.client?.dob) return "";
          const date = new Date(row.client.dob);
          return date.toLocaleDateString("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        },
      },
      { header: "Doctor Name", accessor: "client.doctorName" },
      { header: "Referral Reason", accessor: "client.referralReason" },
      {
        header: "Intake Status",
        accessor: (row) =>
          row.statusHistory?.length > 0
            ? row.statusHistory[row.statusHistory.length - 1].status
            : "Unknown",
      },
      {
        header: "Linked To Client",
        accessor: (row) => (row.clientId ? "Yes" : "No"),
      },
      {
        header: "Can Edit",
        accessor: (row) => (row.isEditable !== false ? "Yes" : "No"),
      },
    ],
    []
  );

  const actions = useMemo(
    () => [
      {
        label: "Create New Intake Form",
        onClick: () => setIsCreateModalOpen(true),
      },
    ],
    []
  );

  return (
    <div className="py-10">
      <div className="mb-4 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <SearchDropdown
              prefix=":intake:"
              displayFields={[
                "initialClientFirstName",
                "initialClientLastName", 
                "initialClientEmail",
                "client.firstName",
                "client.lastName",
                "currentStatus",
              ]}
              onSelection={(intake) => {
                if (intake) {
                  setSelectedIntake(intake);
                }
              }}
              placeholder="Search intakes by client name..."
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-48 rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
            <option value="all">All Intakes</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      <Table
        columns={columns}
        data={filteredIntakes}
        onRowClick={handleRowClick}
        title="Intakes"
        description="A list of all intake forms."
        actions={actions}
      />
      {selectedIntake && (
        <IntakeDetailsModal
          intake={selectedIntake}
          onClose={handleModalClose}
        />
      )}

      {/* Modal to choose client type */}
      <Transition.Root show={isCreateModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setIsCreateModalOpen(false);
            setShowNewClientForm(false);
            setUseExistingClient(false);
            setSelectedClient(null);
            setNewClientInfo({
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
            });
          }}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pt-5 pb-4 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="">
                    <div className="mt-3 text-center sm:mt-0">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900">
                        Create New Intake Form
                      </Dialog.Title>
                      <div className="mt-4">
                        <p className="text-sm text-gray-500">
                          Choose whether to use an existing client or create a
                          new one.
                        </p>
                        <div className="mt-4 space-y-4">
                          <button
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-sm hover:bg-indigo-700"
                            onClick={() => {
                              setUseExistingClient(true);
                            }}>
                            Use Existing Client
                          </button>

                          {useExistingClient && (
                            <div className="mt-4">
                              <SearchDropdown
                                prefix=":client:"
                                displayFields={[
                                  "firstName",
                                  "lastName",
                                  "email",
                                  "phoneNumbers",
                                ]}
                                onSelection={(client) => {
                                  if (client) {
                                    setSelectedClient(client);
                                    setUseExistingClient(false); // Hide dropdown once client is selected
                                  }
                                }}
                                placeholder="Search for an existing client"
                              />
                            </div>
                          )}

                          {selectedClient && (
                            <div className="mt-4">
                              <p className="text-sm text-gray-700">
                                Selected Client:{" "}
                                <span className="font-medium">
                                  {selectedClient.firstName}{" "}
                                  {selectedClient.lastName}
                                </span>
                              </p>

                              {/* Create Intake Button - Only Appears After Selection */}
                              <button
                                className="mt-2 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700"
                                onClick={() => {
                                  handleCreateExistingClientIntake(
                                    selectedClient
                                  );
                                  setSelectedClient(null); // Reset after creating intake
                                  setIsCreateModalOpen(false);
                                }}>
                                Create Intake Form for{" "}
                                {selectedClient.firstName}{" "}
                                {selectedClient.lastName}
                              </button>
                            </div>
                          )}

                          <button
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-sm hover:bg-gray-700"
                            onClick={() => {
                              setShowNewClientForm(true);
                            }}>
                            Use New Client
                          </button>

                          {/* New Client Information Form */}
                          {showNewClientForm && (
                            <div className="mt-4 space-y-4 border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-900">
                                Optional Client Information
                              </h4>
                              <p className="text-xs text-gray-500">
                                Providing this information helps identify the intake form before the client completes it.
                              </p>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    First Name
                                  </label>
                                  <input
                                    type="text"
                                    value={newClientInfo.firstName}
                                    onChange={(e) =>
                                      setNewClientInfo({
                                        ...newClientInfo,
                                        firstName: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="First name"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Last Name
                                  </label>
                                  <input
                                    type="text"
                                    value={newClientInfo.lastName}
                                    onChange={(e) =>
                                      setNewClientInfo({
                                        ...newClientInfo,
                                        lastName: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Last name"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={newClientInfo.email}
                                  onChange={(e) =>
                                    setNewClientInfo({
                                      ...newClientInfo,
                                      email: e.target.value,
                                    })
                                  }
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-indigo-500 focus:border-indigo-500"
                                  placeholder="email@example.com"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Phone
                                </label>
                                <input
                                  type="tel"
                                  value={newClientInfo.phone}
                                  onChange={(e) =>
                                    setNewClientInfo({
                                      ...newClientInfo,
                                      phone: e.target.value,
                                    })
                                  }
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-indigo-500 focus:border-indigo-500"
                                  placeholder="(555) 123-4567"
                                />
                              </div>

                              <div className="flex space-x-3 pt-2">
                                <button
                                  className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-sm hover:bg-gray-50"
                                  onClick={handleNewClientFormCancel}>
                                  Back
                                </button>
                                <button
                                  className="flex-1 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-sm hover:bg-indigo-700"
                                  onClick={handleNewClientFormSubmit}>
                                  Create Intake Form
                                </button>
                              </div>
                            </div>
                          )}
                          
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* URL Modal */}
      <Transition.Root show={isUrlModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={handleUrlModalClose}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900">
                        Share URL with Client
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Copy the link below to share with the client:
                        </p>
                        <div className="mt-4 flex items-center space-x-4">
                          <a
                            href={generatedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-500 underline break-all">
                            {generatedUrl}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-sm border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={handleUrlModalClose}>
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default IntakesPage;

